import React, { useEffect, useMemo } from "react";

/**
 * Roughly the height of the list footer (including chatbox and actions).
 *
 * TODO: hardcoding this is a maintenance hazard, but it makes the code a lot simpler. Reading the height
 * live is particularly problematic because at message-send-time the chatbox can be taller than it is
 * when the message streams in, and we want the post-send short height. So if this becomes an issue,
 * we should do something like measure it and cache on mount.
 */
const listFooterHeightOffset = 172;

/**
 * Roughly equal to the height of a user message (desktop). Ensures that if you scroll all the way to the bottom,
 * you're not completely cutting off the list.
 */
const messageHeightOffset = 92;

/**
 * Allows us to scroll the most recently sent in-session user message (the "active user message")
 * to the top of the message list.
 *
 * Does two things:
 * 1. Provides a spacer height so there's enough space below the user message to scroll it to the top.
 * 2. Actually perform the scroll.
 *
 * We currently use a fixed height for the spacer, just to keep things simple. Note that that means that
 * you can manually scroll down to essentially an arbitrary position in the list.
 *
 * Other things we've tried/considered:
 * 1. Set a min height on the message after the user messsage.
 *    -> Doesn't work with multi-message responses.
 * 2. Chunk the item list into sections for user message sent in-session, then set the min-height of the
 *    chunk container to the height of the list.
 *    -> Works, but making the list non-flat is restictive, e.g. sticky date headers are hard.
 * 3. Use a spacer, but calculate its height dynamically.
 *    -> Doable, just more complex. When do you remeasure? We'll likely implement this to polish excess scrolling if needed.
 */
export const useActiveChatScroll = ({
  scrollContainerRef,
  activeUserMessageRef,
  activeUserMessageId,
}: {
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  activeUserMessageRef: React.RefObject<HTMLElement | null>;
  /** Id of the current active user message; the scroll keys on it. */
  activeUserMessageId: number | undefined;
}) => {
  const spacerHeight = useMemo(() => {
    if (!scrollContainerRef.current) {
      return 0;
    }

    const containerHeight = scrollContainerRef.current.clientHeight;

    // Calculate the height needed to push content up so the active message is at the top
    const otherElHeight = listFooterHeightOffset + messageHeightOffset;
    const calculatedHeight = Math.max(0, containerHeight - otherElHeight);

    console.debug("calculated spacer height", { calculatedHeight, otherElHeight, containerHeight });

    return calculatedHeight;
  }, [scrollContainerRef.current]);

  // When a new active chat message is rendered, scroll it to the top of the
  // list. Keyed on the message ID, not the DOM node: `ref.current` read as a
  // dependency is captured at render time, before the ref attaches, so an
  // effect keyed on it only fires if some later re-render happens to observe
  // the new node — a race that server-written echoes lost (their render isn't
  // followed by the send path's re-renders). The id changes in the same commit
  // that attaches the ref, so effects keyed on it always see the node.
  useEffect(() => {
    if (activeUserMessageId === undefined) return;

    // rAF just to be safe
    requestAnimationFrame(() => {
      if (activeUserMessageRef.current) {
        console.debug("autoscroll to active message", activeUserMessageRef.current);

        // note: because we use scrollIntoView instead of just scrolling to the list bottom,
        // we don't have to precisely calculate the spacer height
        activeUserMessageRef.current.scrollIntoView({ behavior: "smooth" });
      }
    });
  }, [activeUserMessageId, activeUserMessageRef]);

  return { spacerHeight };
};
